import { computed, ref, type ComputedRef } from 'vue'

import { useGptPublicLinkQueries } from '@/apiClient/queries/useGptPublicLinkQueries'
import { useModal } from '@/composables/useModal'

/**
 * Drives the GPT context-menu "web access" entry: its dynamic label
 * ("Publish to web" vs "Manage web access") and the modal that opens it.
 *
 * The label's published state comes from `GET /gpts/{id}/public-link` rather than
 * the payload's `is_web_published`: this menu also renders from detail payloads
 * (`GET /gpts/{id}` — see TheTopbar), which don't compute that field and always
 * report `false`. The request is therefore deferred until the menu is opened
 * (`onMenuToggle`) — never one per card — and only fires when the viewer may
 * manage links (`canManage`, i.e. platform admin), since the endpoint is
 * admin-only and would 403 otherwise. Once the backend also populates
 * `is_web_published` on the detail payload, this fetch can go away entirely.
 *
 * @param gptId     the GPT id
 * @param canManage whether the current user may manage public links (admin)
 */
export function useGptPublicLinkAction(
  gptId: ComputedRef<string>,
  canManage: ComputedRef<boolean>,
) {
  const modal = useModal()
  const isMenuOpen = ref(false)

  const { status } = useGptPublicLinkQueries()
  const linkStatus = status(
    computed(() =>
      isMenuOpen.value && canManage.value ? gptId.value : undefined,
    ),
  )

  const isPublished = computed(() => linkStatus.data.value?.is_active === true)
  const label = computed(() =>
    isPublished.value ? 'Manage web access' : 'Publish to web',
  )

  const onMenuToggle = () => {
    isMenuOpen.value = true
  }

  return { modal, isPublished, label, onMenuToggle }
}
